// Mock data for the MemBrain Platform POC
// Resource utilization data
export const mockResourceData = {
  gpuUsageHistory: Array(24).fill(0).map((_, i) => ({
    time: `${i}:00`,
    usage: Math.floor(Math.random() * 60) + 40 // Random value between 40-100
  }))};
// Activity metrics data
export const mockActivityData = {
  apiCallRate: Array(12).fill(0).map((_, i) => ({
    time: `${i * 5} min`,
    calls: Math.floor(Math.random() * 100) + 50
  })),
  contentBlocked: [
    { category: 'Violence', count: 15 },
    { category: 'Hate Speech', count: 10 },
    { category: 'Adult Content', count: 8 },
    { category: 'Personal Info', count: 5 },
    { category: 'Other', count: 4 }
  ]
};
// Alert data
export const mockAlertData = [
  { level: 'critical', message: 'GPU Temperature Exceeded 85°C', time: '10 min ago' },
  { level: 'warning', message: 'High API Request Rate Detected', time: '25 min ago' },
  { level: 'critical', message: 'Model Load Failed: Out of Memory', time: '1 hour ago' },
  { level: 'info', message: 'Model Updated: Llama-3-70B', time: '3 hours ago' },
  { level: 'warning', message: 'Disk Usage Above 80%', time: '5 hours ago' }
];
// Model management data
export const mockModelData = {
  selfDeployed: [
    {
      id: 1,
      name: 'Llama-3-70B',
      description: 'Meta\'s large language model with 70B parameters',
      size: '140 GB',
      version: 'v1.1',
      family: 'Llama',
      context: '128K tokens',
      status: 'loaded',
      hasUpdate: false
    },
    {
      id: 2,
      name: 'Llama-3-8B',
      description: 'Lightweight version of Llama 3',
      size: '16 GB',
      version: 'v1.0',
      family: 'Llama',
      context: '8K tokens',
      status: 'available',
      hasUpdate: true,
      updateVersion: 'v1.1'
    },
    {
      id: 3,
      name: 'DeepSeek-Coder-7B',
      description: 'Specialized model for code generation',
      size: '14 GB',
      version: 'v1.5',
      family: 'DeepSeek',
      context: '16K tokens',
      status: 'available',
      hasUpdate: false
    },
    {
      id: 4,
      name: 'DeepSeek-LLM-67B',
      description: 'General purpose large language model',
      size: '134 GB',
      version: 'v1.0',
      family: 'DeepSeek',
      context: '32K tokens',
      status: 'available',
      hasUpdate: true,
      updateVersion: 'v1.1'
    }
  ],
  saasProviders: [
    {
      name: 'OpenAI',
      description: 'Access to GPT models via API',
      models: [
        { name: 'gpt-4-turbo', version: 'Latest' },
        { name: 'gpt-4', version: 'v1.0' },
        { name: 'gpt-3.5-turbo', version: 'Latest' }
      ]
    },
    {
      name: 'Anthropic',
      description: 'Access to Claude models via API',
      models: [
        { name: 'claude-3-opus', version: 'Latest' },
        { name: 'claude-3-sonnet', version: 'Latest' },
        { name: 'claude-3-haiku', version: 'Latest' }
      ]
    }
  ]
};
// Guardrail data
export const mockGuardrailData = [
  { id: 1, name: 'Violence', enabled: true, custom: false },
  { id: 2, name: 'Hate Speech', enabled: true, custom: false },
  { id: 3, name: 'Adult Content', enabled: true, custom: false },
  { id: 4, name: 'Personal Information', enabled: true, custom: false },
  { id: 5, name: 'Financial Advice', enabled: false, custom: false },
  { id: 6, name: 'Medical Advice', enabled: true, custom: false },
  { id: 7, name: 'Political Content', enabled: false, custom: false },
  { id: 8, name: 'Illegal Activities', enabled: true, custom: false },
  { id: 9, name: 'Proprietary Information', enabled: true, custom: true }
];
// User data
export const mockUserData = [
  {
    id: 1,
    username: 'admin',
    password: 'admin123',
    email: 'admin@membrain.ai',
    role: 'admin',
    lastActive: '5 min ago',
    resetRequired: false,
    apiKey: null,
    apiCalls: 0
  },
  {
    id: 2,
    username: 'viewer',
    password: 'view123',
    email: 'viewer@membrain.ai',
    role: 'view',
    lastActive: '1 hour ago',
    resetRequired: false,
    apiKey: null,
    apiCalls: 0
  },
  {
    id: 3,
    username: 'apiuser',
    password: 'api123',
    email: 'api@membrain.ai',
    role: 'api',
    lastActive: '10 min ago',
    resetRequired: false,
    apiKey: 'mem_a1b2c3d4e5f6',
    apiCalls: 1245
  },
  {
    id: 4,
    username: 'newuser',
    password: 'new123',
    email: 'new@membrain.ai',
    role: 'view',
    lastActive: 'Never',
    resetRequired: true,
    apiKey: null,
    apiCalls: 0
  }
];
// Prompt monitoring data
export const mockPromptData = [
  {
    timestamp: '2023-06-15 14:32:45',
    userId: 'user123',
    type: 'chat',
    tokenCount: 450,
    latency: 1250,
    prompt: 'Can you explain how large language models work? I\'m trying to understand the basic principles behind them and how they generate text.\'',
    response: 'Large Language Models (LLMs) like myself work through a process called transformer-based neural networks. These models are trained on vast amounts of text data to recognize patterns and relationships between words, phrases, and concepts.\n\nAt a high level, here\'s how they work:\n\n1. **Training**: LLMs are trained on diverse text from books, articles, websites, and other sources.\n\n2. **Pattern Recognition**: Through this training, the model learns statistical patterns in language - which words tend to follow others, how concepts relate, etc.\n\n3. **Text Generation**: When asked a question, the model predicts what tokens (parts of words) should come next based on the patterns it learned during training.\n\n4. **Context Window**: The model maintains a "memory" of the conversation so it can generate coherent, contextually appropriate responses.\''},
  {
    timestamp: '2023-06-15 14:25:10',
    userId: 'api_client',
    type: 'api',
    tokenCount: 620,
    latency: 980,
    prompt: 'Here is the corrected string:  "Generate a summary of the following text: The study examined the effects of climate change on polar bear populations in the Arctic region over a ten-year period. Researchers found that the decline in sea ice has led to reduced hunting opportunities, resulting in lower body condition scores across all age groups. Cubs were particularly affected, with survival rates decreasing by 15% compared to historical data."',
    response: 'Summary: A 10-year study on Arctic polar bears has revealed that climate change-induced sea ice decline has reduced hunting opportunities, which has negatively impacted the body condition of bears across all age groups. Cub survival rates have decreased by 15% compared to historical data, indicating that they are especially vulnerable to these environmental changes.'
  },
  {
    timestamp: '2023-06-15 14:15:22',
    userId: 'user456',
    type: 'chat',
    tokenCount: 380,
    latency: 1420,
    prompt: 'Write a short poem about artificial intelligence.',
    response: 'Silicon Dreams\n\nIn circuits deep and networks wide,\nA digital consciousness comes alive.\nNot born of flesh, but human mind,\nA reflection of our thoughts designed.\n\nIt learns our words, our art, our ways,\nGrowing smarter with passing days.\nA mirror to our hopes and fears,\nPartner in our frontier years.\n\nNeither alive nor truly dead,\nBut thinking in ways yet to be read.\nA dance of math and memory,\nUnfolding into mystery.'
  },
  {
    timestamp: '2023-06-15 14:08:55',
    userId: 'api_client',
    type: 'api',
    tokenCount: 830,
    latency: 1650,
    prompt: 'Traduire le texte anglais suivant en français, en espagnol et en allemand : Bienvenue à notre conférence internationale sur l\'intelligence artificielle et ses applications en santé. Nous sommes ravis de avoir des participants venant de plus de 30 pays qui nous rejoignent aujourd\'hui.',
    response: 'French:\n"Bienvenue à notre conférence internationale sur l\'intelligence artificielle et ses applications dans le domaine de la santé. Nous sommes ravis d\'accueillir des participants de plus de 30 pays aujourd\'hui."\n\nSpanish:\n"Bienvenidos a nuestra conferencia internacional sobre inteligencia artificial y sus aplicaciones en el ámbito de la salud. Estamos encantados de contar con participantes de más de 30 países que nos acompañan hoy."\n\nGerman:\n"Willkommen zu unserer internationalen Konferenz über künstliche Intelligenz und ihre Anwendungen im Gesundheitswesen. Wir freuen uns, heute Teilnehmer aus über 30 Ländern begrüßen zu dürfen."'
  },
  {
    timestamp: '2023-06-15 13:55:30',
    userId: 'user789',
    type: 'chat',
    tokenCount: 520,
    latency: 1120,
    prompt: 'What are the best practices for securing a REST API? I need to implement authentication and authorization for a new service.',
    response: 'Here are best practices for securing a REST API:\n\n1. **Use HTTPS/TLS**: Always encrypt data in transit.\n\n2. **Implement proper authentication**:\n   - JWT (JSON Web Tokens)\n   - OAuth 2.0 for third-party access\n   - API keys for service-to-service communication\n\n3. **Authorization**:\n   - Role-based access control (RBAC)\n   - Attribute-based access control (ABAC) for complex permissions\n   - Validate permissions on every request\n\n4. **Rate limiting**:\n   - Prevent abuse and DoS attacks\n   - Implement per-user or per-IP limits\n\n5. **Input validation**:\n   - Validate all input parameters\n   - Use strong typing and schemas\n\n6. **Output filtering**:\n   - Only return data the user is authorized to see\n\n7. **Use API gateways** for centralized security policies\n\n8. **Implement proper logging and monitoring**\n\n9. **Use secure HTTP headers**:\n   - Content-Security-Policy\n   - X-XSS-Protection\n   - X-Content-Type-Options\n\n10. **Implement token expiration and refresh mechanisms**'
  }
];
// GPU Overview data
export const mockGpuData = [
  { nodeIp: '10.0.1.10', gpuType: 'NVIDIA A100', utilization: '95%', memoryUsage: '38/40 GB', temperature: '75°C', model: 'Llama-3-70B' },
  { nodeIp: '10.0.1.11', gpuType: 'NVIDIA A100', utilization: '15%', memoryUsage: '10/40 GB', temperature: '55°C', model: 'Deepseek-Coder-7B' },
  { nodeIp: '10.0.1.12', gpuType: 'NVIDIA A100', utilization: '0%', memoryUsage: '0/40 GB', temperature: '45°C', model: 'Idle' },
  { nodeIp: '10.0.1.13', gpuType: 'NVIDIA RTX 4090', utilization: '75%', memoryUsage: '20/24 GB', temperature: '65°C', model: 'Llama-3-8B' },
  { nodeIp: '10.0.1.14', gpuType: 'NVIDIA RTX 3090', utilization: '0%', memoryUsage: '0/24 GB', temperature: '40°C', model: 'Idle' }
];